import React, { useState, useEffect } from 'react';
import {
  Modal, Descriptions, Tag, Spin, notification, Button,
  Tabs, Table, Divider, Statistic, Card, Row, Col,
  Badge, Typography, Steps
} from 'antd';
import {
  UserOutlined, EnvironmentOutlined, PhoneOutlined,
  MailOutlined, CalendarOutlined, DollarOutlined,
  CheckCircleOutlined, CloseCircleOutlined, PrinterOutlined,
  FileTextOutlined, CreditCardOutlined, HomeOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import ordersService from '../../../../services/ordersService';
import moment from 'moment';
import userRoleUtils from '../../../../utils/userRoleUtils';
import OrderLogsPanel from '../OrderLogsPanel';
import OrderCleaningReport from '../OrderCleaningReport';
import OrderTaskNoteButton from '../OrderTaskNoteButton';
import { TimerIcon, User2Icon } from 'lucide-react';
import { getOrderDriverNote } from '../../../../utils/onfleetUtils';
import {
  canCaptureSavedCard as isSavedCardCaptureEligible,
  getSavedCardActionLabel,
  getSavedCardActionTooltip,
} from '../../../../utils/orderPaymentAction';
import {
  calculateOrderItemSubtotal,
  getPersistedDiscountAmount,
  resolveOrderShipping,
} from '../../../../utils/orderEditTotals';
import { getIndependentOrderProgress } from '../../../../utils/orderProgress';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Step } = Steps;

const ViewModal = ({ visible, orderId, onCancel, users, canPrintLabel = false, onCaptureSavedCard }) => {
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && orderId) fetchOrderData();
    return () => {
      setOrder(null);
      setOrderItems([]);
    };
  }, [visible, orderId]);
  

  const fetchOrderData = async () => {
    try {
      setLoading(true);
      const response = await ordersService.getOrderByIdAdmin(orderId);
      setOrder(response.order || response);
      setOrderItems(response.order.order_products || response.orderItems || []);
    } catch (error) {
      notification.error({
        message: 'Error loading order',
        description: error.message || 'Failed to load order data'
      });
    } finally {
      setLoading(false);
    }
  };

  const printOrder = () => {
    window.open(`/admin/order/detail-print/${orderId}`, '_blank');
  };

  const printLabel = () => {
    const printContent = document.getElementById('print-label-content').innerHTML;
    const printWindow = window.open('', '_blank', 'width=400,height=600');

    printWindow.document.write(`
      <html>
        <head>
          <title>Order #${order?.id}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 10px; }
            h2 { margin-bottom: 5px; }
            p { margin: 4px 0; }
            ul { padding-left: 20px; }
            li { margin-bottom: 3px; }
          </style>
        </head>
        <body>
          ${document.getElementById('print-label-content').innerHTML}
        </body>
      </html>`);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };
  // Helper functions for rendering
  const formatPrice = (price) => {
    return parseFloat(price || 0).toFixed(2);
  };

  const getOrderStatusTag = (status) => {
    switch (status) {
      case 'Pending':
        return <Tag color="gold">Pending</Tag>;
      case 'Scheduled':
        return <Tag color="blue">Scheduled</Tag>;
      case 'En-route':
      case 'En-Route':
      case 'On-Route':
      case 'In Progress':
        return <Tag color="orange">{status}</Tag>;
      case 'Completed':
        return <Tag color="green">Completed</Tag>;
      case 'Collected':
        return <Tag color="purple">Collected</Tag>;
      case 'Cancelled':
        return <Tag color="red">Cancelled</Tag>;
      case 'Failed':
        return <Tag color="volcano">Failed</Tag>;
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const getPaymentStatusTag = (status) => {
    switch (status) {
      case 1:
        return <Tag color="green">Paid</Tag>;
      case 2:
        return <Tag color="red">Refunded</Tag>;
      case 3:
        return <Tag color="orange">Partial Refund</Tag>;
      case 0:
      default:
        return <Tag color="volcano">Unpaid</Tag>;
    }
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format('DD MMM YYYY');
  };

  const placedByUser = users?.find(user => user.id === order?.placer_id);
  const placedByRoleNames = placedByUser?.roles?.map(role => role.name) || [];
  const placedByIsInternal = placedByRoleNames.some(role => ['admin', 'superadmin', 'subadmin', 'dispatcher'].includes(String(role).toLowerCase()));
  const fallbackPlacedByName = placedByIsInternal
    ? 'ADMIN'
    : (placedByUser?.name || (order?.placer_id && order?.placer_id !== order?.user_id ? 'ADMIN' : `${order?.delivery_first_name || ''} ${order?.delivery_last_name || ''}`.trim()));
  const placedByName = order?.placed_by_display || fallbackPlacedByName || 'Client';
  const placedByEmail = order?.placed_by_is_internal ? '' : (order?.placed_by_email || (placedByIsInternal ? '' : placedByUser?.email));
  const canCaptureSavedCard = Boolean(
    onCaptureSavedCard
    && order
    && isSavedCardCaptureEligible(order)
  );
  const driverNote = getOrderDriverNote(order);

  const renderProgressSteps = (progress, type) => {
    if (!progress) {
      return <Text type="secondary">No linked cleaning progress is available yet.</Text>;
    }

    const isCleaning = type === 'cleaning';
    const icons = isCleaning
      ? [<ClockCircleOutlined />, <CalendarOutlined />, <ClockCircleOutlined />, <CheckCircleOutlined />]
      : [<CalendarOutlined />, <ClockCircleOutlined />, <CheckCircleOutlined />, <HomeOutlined />];
    const descriptions = isCleaning
      ? ['Awaiting cleaning schedule', 'Cleaner scheduled', 'Cleaning in progress', 'Cleaning completed']
      : [
          order?.property_date ? getFormattedDate(order.property_date) : '',
          'Delivery in progress',
          'Order delivered',
          'Items returned',
        ];

    return (
      <>
        <div className="mb-3">
          <Text type="secondary">Current status: </Text>
          {getOrderStatusTag(progress.status)}
        </div>
        {progress.isError ? (
          <Steps direction="horizontal" status="error" current={0} className="mt-4">
            <Step title={progress.steps[0]} icon={icons[0]} />
            <Step
              title={progress.status}
              description={`${isCleaning ? 'Cleaning' : 'Linen order'} could not be completed`}
              icon={<CloseCircleOutlined />}
              status="error"
            />
          </Steps>
        ) : (
          <Steps direction="horizontal" current={progress.current} className="mt-4">
            {progress.steps.map((title, index) => (
              <Step key={`${type}-${title}`} title={title} description={descriptions[index]} icon={icons[index]} />
            ))}
          </Steps>
        )}
      </>
    );
  };

  const orderProgressSteps = () => {
    const progress = getIndependentOrderProgress(order);

    return (
      <>
        <Divider orientation="left">{progress.linen.label}</Divider>
        {renderProgressSteps(progress.linen, 'linen')}
        <Divider orientation="left">Cleaning progress</Divider>
        {renderProgressSteps(progress.cleaning, 'cleaning')}
      </>
    );
  };

  const itemsColumns = [
    {
      title: 'Item',
      dataIndex: 'order_products_name',
      key: 'order_products_name',
      render: (text, record) => (
        <div>
          <div className="font-medium">{text || record.name}</div>
          {record.product_description && (
            <div className="text-xs text-gray-500">{record.product_description}</div>
          )}
        </div>
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'order_products_qty',
      key: 'order_products_qty',
      width: 100,
      align: 'center'
    },
    {
      title: 'Price',
      dataIndex: 'order_products_price',
      key: 'order_products_price',
      width: 120,
      align: 'right',
      render: (text, record) => `£${formatPrice(text || record.order_products_price || 0)}`
    },
    {
      title: 'Total',
      key: 'total',
      width: 120,
      align: 'right',
      render: (_, record) => {
        const price = parseFloat(record.order_products_price || 0);
        const quantity = parseFloat(record.order_products_qty || 0);
        return `£${formatPrice(price * quantity)}`;
      }
    }
  ];

  // Format full address to display in a single line
  const getFullAddress = () => {
    if (!order) return '';

    let address = '';
   if (order.order_property?.apartment_number) {
      address += order.order_property.apartment_number + ', ';
    }
    if (order.delivery_apartment_name) {
      address += order.delivery_apartment_name + ', ';
    }

    if (order.delivery_address_1) {
      address += order.delivery_address_1;
    }

    if (order.delivery_address_2) {
      address += ', ' + order.delivery_address_2;
    }

    if (order.delivery_city) {
      address += ', ' + order.delivery_city;
    }

    if (order.delivery_zip_code) {
      address += ', ' + order.delivery_zip_code;
    }

    return address;
  };

  return (
    <Modal
      title={
        <div className="flex justify-between items-center">
          <span>Order Details {orderId ? `#${orderId}` : ''}</span>
          <div className="flex gap-2">
            {canPrintLabel && (
              <Button
                icon={<PrinterOutlined />}
                onClick={printLabel}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Print Label
              </Button>
            )}
            {/*   <Button 
            type="primary" 
            icon={<PrinterOutlined />} 
            onClick={printOrder}
            className="bg-green-600 hover:bg-green-700"
          >
            Print
          </Button> */}
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={900}
      footer={[<Button key="back" onClick={onCancel}>Close</Button>]}
    >
      <Spin spinning={loading}>
        {order ? (
          <Tabs defaultActiveKey="details">
            <TabPane
              tab={<span><FileTextOutlined /> Order Details</span>}
              key="details"
            >
              <Card className="mb-4">
                <div id="print-label-content" style={{ display: 'none' }}>
                  <div style={{ padding: '20px', fontFamily: 'Arial', fontWeight: 'regular' }}>
                    <h1>Order #:{order?.id}</h1>
                    <h1>Property #:{order?.order_property?.property_title}</h1>
                    <h2> Customer:  {order?.delivery_first_name} {order?.delivery_last_name}</h2>
                    <h2> Address:  {order?.delivery_address_1}, {order?.delivery_city}, {order?.delivery_zip_code}</h2>
                    <Divider />
                    {/*  <ul>
      {orderItems.map(item => (
        <li key={item.id}>{item.order_products_qty} x {item.order_products_name}</li>
      ))}
    </ul> */}
                  </div>
                </div>
                {/* Delivery Address at the top */}
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div className="mb-2">
                      <Text type="secondary">Delivery Address</Text>
                    </div>
                    <div className="flex items-center">
                      <EnvironmentOutlined className="mr-2" />
                      {getFullAddress()}
                    </div>
                  </Col>

                  {/* propery property_description  show here not show if is NULL or null */}

                  {(order.order_property?.property_description && order.order_property?.property_description !== null && order.order_property?.property_description !== 'null' && order.order_property?.property_description !== 'NULL' )&& (

                    <Col span={12}>
                      <div className="mb-2">
                        <Text type="secondary">Property Description</Text>
                      </div>
                      <div className="flex items-center">
                        <HomeOutlined className="mr-2" />
                        {order.order_property?.property_description}
                      </div>
                    </Col>

                  )}
                </Row>
                {order?.order_property?.property_title && (
                  <Row gutter={[16, 16]}>
                    <Col span={12}>
                      <div className="mb-2">
                        <Text type="secondary">Property ID</Text>
                      </div>
                      <div className="flex items-center">
                        <HomeOutlined className="mr-2" />
                        {order?.order_property?.property_title}
                      </div>
                    </Col>
                    {/* property have placer_id so we can find user who placed order and show here */}
                    { order?.placer_id && (
                      <Col span={12}>
                        <div className="mb-2">
                          <Text type="secondary">Placed By</Text>
                        </div>
                        <div className="flex items-center">
                          <User2Icon className="mr-2" />
                         <h3>{placedByName || 'Client'}</h3><br />
                          {placedByEmail && (
                            <p className="text-xs text-gray-500 ml-2">
                              {placedByEmail}
                            </p>
                          )}
                        </div>
                         <div className="flex items-center">
                          <TimerIcon className="mr-2" />
                         <h3> {moment(order?.created_at).format('DD MMM YYYY H:m')} </h3><br />
                         
                        </div>
                      </Col>
                    )}
                  </Row>
                )}

                <Divider />

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic
                      title="Order ID"
                      value={`#${order.id}`}
                      valueStyle={{ fontWeight: 'bold' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Total Amount"
                      value={`£${formatPrice(order.order_total)}`}
                      valueStyle={{ color: '#3f8600', fontWeight: 'bold' }}
                      prefix={<DollarOutlined />}
                    />
                  </Col>
                  <Col span={12}>
                    <div className="mb-2">
                      <Text type="secondary">Order Status</Text>
                    </div>
                    {getOrderStatusTag(order.order_status)}
                  </Col>
                  <Col span={12}>
                    <div className="mb-2">
                      <Text type="secondary">Payment Status</Text>
                    </div>
                    {getPaymentStatusTag(order.payment_status)}
                  </Col>
                  <Col span={12}>
                    <div className="mb-2">
                      <Text type="secondary">Order Date</Text>
                    </div>
                    <div className="flex items-center">
                      <CalendarOutlined className="mr-2" />
                      {getFormattedDate(order.property_date)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="mb-2">
                      <Text type="secondary">Placement Date</Text>
                    </div>
                    <div className="flex items-center">
                      <ClockCircleOutlined className="mr-2" />
                      {getFormattedDate(order.created_at)}
                    </div>
                  </Col>
                </Row>
              </Card>

              {orderProgressSteps()}

              <div className="mt-3">
                <OrderTaskNoteButton order={order} viewOnly showLabel />
              </div>

              {driverNote && (
                <Card className="mt-4" size="small" title="Driver Note">
                  <Text>{driverNote}</Text>
                </Card>
              )}

              <Divider orientation="left">Order Items</Divider>

              <Table
                columns={itemsColumns}
                dataSource={orderItems}
                rowKey={(record) => record.id || record.product_id}
                pagination={false}
                summary={(pageData) => {
                  const subtotal = calculateOrderItemSubtotal(pageData);
                  const discount = getPersistedDiscountAmount(order);
                  const shipping = resolveOrderShipping(order);

                  return (
                    <>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={3} align="right">
                          <strong>Subtotal:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <strong>£{formatPrice(subtotal)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                      {discount > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3} align="right">
                            Discount:
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            -£{formatPrice(discount)}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                      {shipping > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3} align="right">
                            Delivery Fee:
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="right">
                            £{formatPrice(shipping)}
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={3} align="right">
                          <strong>Total:</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <strong>£{formatPrice(order.order_total)}</strong>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  );
                }}
              />
            </TabPane>

            <TabPane
              tab={<span><UserOutlined /> Customer Information</span>}
              key="customer"
            >
              <Card className="mb-4">
                <Title level={5} className="mb-4">Customer Details</Title>
                <Descriptions bordered column={1}>
                  <Descriptions.Item label="Name">
                    <span className="flex items-center">
                      <UserOutlined className="mr-2" />
                      {order.delivery_first_name} {order.delivery_last_name}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    <span className="flex items-center">
                      <MailOutlined className="mr-2" />
                      {order.order_email}
                    </span>
                  </Descriptions.Item>
                  {order.delivery_phone_no && (
                    <Descriptions.Item label="Phone">
                      <span className="flex items-center">
                        <PhoneOutlined className="mr-2" />
                        {order.delivery_phone_no}
                      </span>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            </TabPane>

            <TabPane
              tab={<span><CreditCardOutlined /> Payment Information</span>}
              key="payment"
            >
              <Card>
                <Row gutter={[16, 24]}>
                  <Col span={24}>
                    <Statistic
                      title="Payment Status"
                      value={
                        order.payment_status === 1 ? 'Paid' :
                          order.payment_status === 2 ? 'Refunded' :
                            order.payment_status === 3 ? 'Partially Refunded' :
                              'Unpaid'
                      }
                      valueStyle={{
                        color: order.payment_status === 1 ? '#3f8600' :
                          order.payment_status === 0 ? '#cf1322' :
                            '#faad14'
                      }}
                      prefix={order.payment_status === 1 ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    />
                  </Col>

                  <Col span={12}>
                    <Statistic
                      title="Order Total"
                      value={`£${formatPrice(order.order_total)}`}
                      prefix={<DollarOutlined />}
                    />
                  </Col>

                  {canCaptureSavedCard && (
                    <Col span={12}>
                      <Button
                        type="primary"
                        icon={<CreditCardOutlined />}
                        title={getSavedCardActionTooltip(order)}
                        onClick={async () => {
                          await onCaptureSavedCard(order.id, order);
                          await fetchOrderData();
                        }}
                      >
                        {getSavedCardActionLabel(order)}
                      </Button>
                      <div className="text-xs text-gray-500 mt-2">
                        {[order.stripe_account_label, order.stripe_card_brand, order.stripe_card_last4 ? `**** ${order.stripe_card_last4}` : null].filter(Boolean).join(' - ')}
                      </div>
                    </Col>
                  )}

                  {order.refund_amount > 0 && (
                    <Col span={12}>
                      <Statistic
                        title="Refunded Amount"
                        value={`£${formatPrice(order.refund_amount)}`}
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Col>
                  )}

                  {order.remaining_amount > 0 && (
                    <Col span={12}>
                      <Statistic
                        title="Remaining Amount"
                        value={`£${formatPrice(order.remaining_amount)}`}
                      />
                    </Col>
                  )}

                  {order.transaction_id && (
                    <Col span={24}>
                      <Descriptions bordered>
                        <Descriptions.Item label="Transaction ID" span={3}>
                          {order.transaction_id}
                        </Descriptions.Item>

                        {order.payment_method && (
                          <Descriptions.Item label="Payment Method" span={3}>
                            {order.payment_method}
                          </Descriptions.Item>
                        )}

                        {order.payment_date && (
                          <Descriptions.Item label="Payment Date" span={3}>
                            {getFormattedDate(order.payment_date)}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Col>
                  )}

                  {order.refund_id && (
                    <>
                      <Col span={24}>
                        <Divider orientation="left">Refund Information</Divider>
                      </Col>

                      <Col span={24}>
                        <Descriptions bordered>
                          <Descriptions.Item label="Refund ID" span={3}>
                            {order.refund_id}
                          </Descriptions.Item>

                          {order.refund_date && (
                            <Descriptions.Item label="Refund Date" span={3}>
                              {getFormattedDate(order.refund_date)}
                            </Descriptions.Item>
                          )}

                          <Descriptions.Item label="Refund Amount" span={3}>
                            £{formatPrice(order.refund_amount || 0)}
                          </Descriptions.Item>
                        </Descriptions>
                      </Col>
                    </>
                  )}
                </Row>
              </Card>
            </TabPane>
 
            <TabPane tab={<span><FileTextOutlined /> Cleaning report</span>} key="cleaning-report">
              <OrderCleaningReport orderId={order.id} />
            </TabPane>
        {userRoleUtils.isAdmin() && <TabPane tab={<span><FileTextOutlined /> Order History & Activity</span>}
              key="logs"
            >
              <OrderLogsPanel orderId={order.id} />
            </TabPane>}

          </Tabs>
        ) : (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
            <p className="ml-3 text-gray-500">Loading order data...</p>
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default ViewModal;
